import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { isNativeApp } from '../utils/platform';
import { TURKISH_COFFEE_KITCHEN } from '../lib/kitchenRoutes';
import './CheckoutPage.css';

/* global Tyro */

export default function CheckoutPage() {
  const { items, total, clearCart, releaseAll, sessionId } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [notes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paySecret, setPaySecret] = useState(null);
  const [payRequestId, setPayRequestId] = useState(null);
  const [orderIds, setOrderIds] = useState(null);
  const [orderResult, setOrderResult] = useState(null);
  const [paymentTotal, setPaymentTotal] = useState(0);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [tyroConfig, setTyroConfig] = useState({ liveMode: false, googlePayMerchantId: '', saveCardEnabled: false });
  const [savedPayMethods, setSavedPayMethods] = useState([]);
  const [saveCard, setSaveCard] = useState(false);
  const [saveCardUpdating, setSaveCardUpdating] = useState(false);
  const [payWithSaved, setPayWithSaved] = useState(true);
  const [selectedPayMethodId, setSelectedPayMethodId] = useState('');
  const tyroRef = useRef(null);
  const payFormRef = useRef(null);
  const payRequestSavesCardRef = useRef(false);
  
  const [orderForLater, setOrderForLater] = useState(false);
  const [pickupTime, setPickupTime] = useState('');
  const [categoryWindows, setCategoryWindows] = useState({});
  const [scheduleForLaterEnabled, setScheduleForLaterEnabled] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [show3dsBrowserFallback, setShow3dsBrowserFallback] = useState(false);

  useEffect(() => {
    if (searchParams.get('cancelled') === '1') {
      setError('Payment was cancelled. You can try again or choose a different payment method.');
    }
  }, [searchParams]);

  useEffect(() => {
    if (user?.name && !name) setName(user.name);
  }, [user]); // eslint-disable-line

  useEffect(() => {
    api.getTyroConfig().then((cfg) => setTyroConfig(cfg)).catch(() => {});
    api.getPublicCategoryWindows().then(setCategoryWindows).catch(() => {});
    api.getMenuStatus().then((s) => {
      if (s.schedule_for_later) setScheduleForLaterEnabled(true);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && tyroConfig.saveCardEnabled) {
      setSaveCard(true);
    } else {
      setSaveCard(false);
    }
  }, [user, tyroConfig.saveCardEnabled]);

  useEffect(() => {
    if (!user || !tyroConfig.saveCardEnabled) {
      setSavedPayMethods([]);
      return;
    }
    let cancelled = false;
    api.getPayMethods()
      .then((rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setSavedPayMethods(list);
        if (list[0]?.pay_method_id) {
          setSelectedPayMethodId(list[0].pay_method_id);
          setPayWithSaved(true);
        }
      })
      .catch(() => {
        if (!cancelled) setSavedPayMethods([]);
      });
    return () => { cancelled = true; };
  }, [user, tyroConfig.saveCardEnabled]);

  const isStandalonePWA = useCallback(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    return isStandalone && isIOS;
  }, []);

  const initTyroForm = useCallback(async (secret, requestIdOverride) => {
    if (!window.Tyro) {
      setError('Payment system failed to load. Please refresh the page.');
      return;
    }

    const activePayRequestId = requestIdOverride ?? payRequestId;

    try {
      const tyro = Tyro({ liveMode: tyroConfig.liveMode });
      tyroRef.current = tyro;
      await tyro.init(secret);

      const googlePayOptions = { enabled: true, merchantInfo: { merchantName: 'St Barbara & St Noufer' } };
      // Only attach the production Google Pay merchant ID when Tyro is in live
      // mode. Pairing a real merchant ID with a sandbox pay request is one of
      // the documented causes of Google Pay's OR_BIBED_11 rejection.
      if (tyroConfig.liveMode && tyroConfig.googlePayMerchantId) {
        googlePayOptions.merchantInfo.merchantId = tyroConfig.googlePayMerchantId;
      }
      console.info('Tyro pay form config:', {
        liveMode: tyroConfig.liveMode,
        googlePayMerchantIdSet: !!googlePayOptions.merchantInfo.merchantId,
      });

      const payForm = tyro.createPayForm({
        options: {
          applePay: { enabled: true, totalLabel: 'St Barbara & St Noufer' },
          googlePay: googlePayOptions,
          creditCardForm: { enabled: true },
        },
      });

      payForm.setWalletPaymentBeginListener(() => {
        setPaymentProcessing(true);
        setError(null);
      });

      payForm.setWalletPaymentCancelledListener(() => {
        setPaymentProcessing(false);
      });

      payForm.setWalletPaymentCompleteListener(async (paymentType, walletError) => {
        if (walletError) {
          console.error('Wallet payment error:', {
            paymentType,
            type: walletError.type,
            errorMessage: walletError.errorMessage,
            full: walletError,
          });
          const { type, errorMessage } = walletError;
          if (type === 'CARD_ERROR') {
            setError(errorMessage || 'Your card was declined.');
          } else if (type === 'SERVER_VALIDATION_ERROR') {
            setError(errorMessage || 'Payment validation failed.');
          } else {
            setError(errorMessage || 'Payment failed. Please try again.');
          }
          setPaymentProcessing(false);
          return;
        }

        try {
          const payRequest = await tyro.fetchPayRequest();
          if (payRequest.status === 'SUCCESS') {
            await api.confirmPayment(orderIds, secret, activePayRequestId);
            clearCart();
            navigateToConfirmation(orderResult);
          } else {
            setError(`Payment status: ${payRequest.status}. Please try again.`);
          }
        } catch (err) {
          setError(err.message || 'Payment verification failed.');
        } finally {
          setPaymentProcessing(false);
        }
      });

      payFormRef.current = payForm;
      await payForm.inject('#tyro-pay-form');
    } catch (err) {
      console.error('Tyro init error:', err);
      const msg = err instanceof Error ? err.message : (typeof err === 'string' ? err : '');
      setError(msg ? `Failed to load payment form: ${msg}. Please try again.` : 'Failed to load payment form. Please try again.');
    }
  }, [tyroConfig, orderIds, orderResult, payRequestId]); // eslint-disable-line

  const tyroFormDivRef = useRef(null);
  const [tyroFormReady, setTyroFormReady] = useState(false);

  const tyroFormRefCallback = useCallback((node) => {
    tyroFormDivRef.current = node;
    if (node) setTyroFormReady(true);
    else setTyroFormReady(false);
  }, []);

  useEffect(() => {
    if (showPaymentForm && paySecret && tyroFormReady && !saveCardUpdating) {
      initTyroForm(paySecret);
    }
  }, [showPaymentForm, paySecret, tyroFormReady, initTyroForm]); // eslint-disable-line

  if (items.length === 0 && !searchParams.get('cancelled') && !showPaymentForm) {
    navigate('/');
    return null;
  }

  const hasCafeItems = items.some((i) => i.venue === 'cafe');
  const hasTurkishCoffee = items.some((i) => i.kitchen === TURKISH_COFFEE_KITCHEN);
  const nameRequired = hasCafeItems || hasTurkishCoffee;

  const timeSlots = (() => {
    const slots = [];
    const now = new Date();
    const nowMins = now.getHours() * 60 + now.getMinutes();
    const pushRange = (startMins, endMins) => {
      const s = Math.ceil(startMins / 15) * 15;
      for (let m = s; m < endMins; m += 15) {
        if (m <= nowMins) continue;
        const h = Math.floor(m / 60);
        const mm = m % 60;
        const ampm = h >= 12 ? 'PM' : 'AM';
        const label = `${h % 12 || 12}:${String(mm).padStart(2, '0')} ${ampm}`;
        const value = `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
        slots.push({ label, value });
      }
    };

    const allWindows = Object.values(categoryWindows);
    let startMins;
    let endMins;
    if (allWindows.length > 0) {
      startMins = allWindows.reduce((min, w) => {
        const [h, m] = (w.available_from || '10:00').split(':').map(Number);
        return Math.min(min, h * 60 + m);
      }, 24 * 60);
      endMins = allWindows.reduce((max, w) => {
        const [h, m] = (w.available_until || '15:00').split(':').map(Number);
        return Math.max(max, h * 60 + m);
      }, 0);
    } else {
      startMins = 8 * 60;
      endMins = 15 * 60;
    }
    pushRange(startMins, endMins);
    if (slots.length === 0) {
      pushRange(8 * 60, 15 * 60);
    }
    return slots;
  })();

  function navigateToConfirmation(result) {
    // Trust only the server-returned group. Falling back to the localStorage
    // active_order_group can attach a fresh order to a group whose previous
    // orders have all been collected, causing old order numbers to reappear.
    // Also stash the customer name so a *different* customer ordering on this
    // device later (shared phone, kiosk, family device) doesn't blindly send
    // add_to_group for someone else's group. The server has the authoritative
    // guard but this avoids the noisy rejection and a momentary cross-binding.
    const ownerName = String(name || 'Guest').trim().toLowerCase();
    if (result.order_group) {
      localStorage.setItem('active_order_group', result.order_group);
      localStorage.setItem('active_order_group_owner', ownerName);
      localStorage.setItem('active_order_path', `/confirmation/group/${result.order_group}`);
      navigate(`/confirmation/group/${result.order_group}`);
    } else {
      localStorage.setItem('active_order_group', result.order_number);
      localStorage.setItem('active_order_group_owner', ownerName);
      localStorage.setItem('active_order_path', `/confirmation/${result.order_number}`);
      navigate(`/confirmation/${result.order_number}`);
    }
  }

  async function handleSubmit(e) {
    if (e) e.preventDefault();
    if (cancelling || submitting) return;
    if (nameRequired && !name.trim()) {
      setError('Name is required for cafe and Turkish coffee orders.');
      return;
    }

    if (!isNativeApp() && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }

    setSubmitting(true);
    setError(null);

    try {
      const activeGroup = localStorage.getItem('active_order_group');
      const activeGroupOwner = localStorage.getItem('active_order_group_owner');
      const currentName = String(name || 'Guest').trim().toLowerCase();

      const orderPayload = {
        customer_name: name || 'Guest',
        items: items.map((i) => ({
          menu_item_id: i.menu_item_id,
          quantity: i.quantity,
          ...(i.options ? { options: i.options } : {}),
          ...(i.variableAmount != null ? { unit_price: i.price } : {}),
        })),
        notes: notes || undefined,
        payment_method: 'card',
        session_id: sessionId,
      };
      const usingSaved = Boolean(
        user && tyroConfig.saveCardEnabled && payWithSaved && selectedPayMethodId
      );
      if (usingSaved) {
        orderPayload.pay_method_id = selectedPayMethodId;
      } else if (user && tyroConfig.saveCardEnabled && saveCard) {
        // Tokenisation is a Pay Request create flag. Apply it here so ticking
        // Save card after typing does not remount Tyro's hosted fields.
        orderPayload.save_card = true;
      }
      if (orderForLater && pickupTime) {
        orderPayload.pickup_time = pickupTime;
      }
      // Only attach add_to_group if the name on this checkout matches the
      // owner who originally created the group. Prevents a different
      // customer ordering on the same device (or a stale localStorage that
      // somehow contaminated this device — shared link, deep link from a
      // push notification, etc.) from being silently merged into someone
      // else's order. If the previous owner is unknown (older app version
      // that didn't record it) we conservatively drop the hint too.
      if (activeGroup && activeGroupOwner && activeGroupOwner === currentName) {
        orderPayload.add_to_group = activeGroup;
      } else if (activeGroup && !activeGroupOwner) {
        // Legacy localStorage from a build before the owner tag existed —
        // clear it rather than risk a cross-customer merge.
        localStorage.removeItem('active_order_group');
        localStorage.removeItem('active_order_group_owner');
        localStorage.removeItem('active_order_path');
      } else if (activeGroup && activeGroupOwner && activeGroupOwner !== currentName) {
        // Different person checking out on a device that still has the
        // previous customer's group cached. Drop the cache and start fresh.
        localStorage.removeItem('active_order_group');
        localStorage.removeItem('active_order_group_owner');
        localStorage.removeItem('active_order_path');
      }

      const result = await api.placeOrder(orderPayload);
      const ids = result.order_ids || (result.orders ? result.orders.map((o) => o.id) : [result.id]);

      if (result.paid) {
        await api.confirmPayment(ids, result.paySecret, result.payRequestId);
        clearCart();
        navigateToConfirmation(result);
        return;
      }

      if (result.paySecret) {
        if (isStandalonePWA()) {
          sessionStorage.setItem('pwa_pay', JSON.stringify({
            paySecret: result.paySecret,
            payRequestId: result.payRequestId,
            orderIds: ids,
            orderResult: result,
            total,
          }));
        }

        // Show the in-app Tyro pay form. If the card actually requires 3DS,
        // handleCardSubmit's catch block detects that and surfaces the
        // SafariViewController fallback button — we should never proactively
        // bounce users out to a browser for ordinary card / Apple Pay /
        // Google Pay payments.
        setPaymentTotal(total);
        setPaySecret(result.paySecret);
        setPayRequestId(result.payRequestId);
        setOrderIds(ids);
        setOrderResult(result);
        payRequestSavesCardRef.current = Boolean(!usingSaved && user && tyroConfig.saveCardEnabled && saveCard);
        setShowPaymentForm(true);
        setSubmitting(false);
        return;
      }

      clearCart();
      navigateToConfirmation(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSaveCardChange(wantSave) {
    if (!orderIds?.length || saveCardUpdating || paymentProcessing) return;
    setSaveCard(wantSave);
    if (Boolean(wantSave) === Boolean(payRequestSavesCardRef.current)) return;
    setSaveCardUpdating(true);
    setError(null);
    try {
      const result = await api.createPayRequest(orderIds, { save_card: wantSave });
      if (!result?.paySecret) {
        throw new Error('Could not update payment for save-card preference');
      }
      setPaySecret(result.paySecret);
      setPayRequestId(result.payRequestId || null);
      if (isStandalonePWA() && orderResult) {
        sessionStorage.setItem('pwa_pay', JSON.stringify({
          paySecret: result.paySecret,
          payRequestId: result.payRequestId,
          orderIds,
          orderResult,
          total: paymentTotal,
        }));
      }
      const formEl = document.getElementById('tyro-pay-form');
      if (formEl) formEl.innerHTML = '';
      tyroRef.current = null;
      payFormRef.current = null;
      payRequestSavesCardRef.current = wantSave;
      await initTyroForm(result.paySecret, result.payRequestId || null);
    } catch (err) {
      setSaveCard(!wantSave);
      setError(err.message || 'Could not update save-card preference. Please try again.');
    } finally {
      setSaveCardUpdating(false);
    }
  }

  async function handleCardSubmit(e) {
    e.preventDefault();
    if (!tyroRef.current) return;

    setPaymentProcessing(true);
    setError(null);

    try {
      await tyroRef.current.submitPay();
      const payRequest = await tyroRef.current.fetchPayRequest();

      if (payRequest.status === 'SUCCESS') {
        await api.confirmPayment(orderIds, paySecret, payRequestId);
        clearCart();
        navigateToConfirmation(orderResult);
      } else {
        const txn = payRequest.transactionResults?.[0];
        const detail = txn ? `${txn.result || ''} ${txn.gatewayCode || ''} ${txn.errorMessage || ''}`.trim() : '';
        setError(`Payment ${payRequest.status}: ${detail || JSON.stringify(payRequest)}`);
      }
    } catch (err) {
      console.error('Tyro submitPay error:', err);
      const errMsg = err?.errorMessage || err?.message || '';
      const is3dsError = errMsg.toLowerCase().includes('3ds') || errMsg.toLowerCase().includes('authentication');

      if (is3dsError && isNativeApp()) {
        setError('Your bank requires extra verification. Tap below to complete payment securely.');
        setShow3dsBrowserFallback(true);
      } else if (is3dsError && isStandalonePWA()) {
        setError('Your bank requires extra verification. Tap below to complete payment in Safari.');
        setShow3dsBrowserFallback(true);
      } else {
        let errStr;
        try { errStr = JSON.stringify(err, Object.getOwnPropertyNames(err)); } catch { errStr = String(err); }
        setError(`Payment error: ${errMsg || errStr}`);
      }
    } finally {
      setPaymentProcessing(false);
    }
  }

  function destroyTyroForm() {
    if (payFormRef.current && typeof payFormRef.current.remove === 'function') {
      try { payFormRef.current.remove(); } catch (_) { /* ignore */ }
    }
    const formEl = document.getElementById('tyro-pay-form');
    if (formEl) formEl.innerHTML = '';

    document.querySelectorAll(
      'iframe[src*="tyro"], iframe[id*="tyro"], iframe[name*="tyro"], [class*="tyro"], [data-tyro]'
    ).forEach((el) => {
      if (!el.closest('.checkout-page')) el.remove();
    });
    document.querySelectorAll(
      'iframe[src*="tyro"], iframe[id*="tyro"], iframe[name*="tyro"]'
    ).forEach((el) => el.remove());
  }

  async function handleCancelPayment() {
    setCancelling(true);
    try {
      destroyTyroForm();

      if (orderIds && orderIds.length > 0) {
        await Promise.all(orderIds.map((id) => api.updateOrderStatus(id, 'cancelled')));
      }
    } catch (err) {
      console.error('Failed to cancel orders:', err);
    }
    setShowPaymentForm(false);
    setPaySecret(null);
    setPayRequestId(null);
    setOrderIds(null);
    setOrderResult(null);
    payRequestSavesCardRef.current = false;
    setPaymentProcessing(false);
    setTyroFormReady(false);
    tyroRef.current = null;
    payFormRef.current = null;
    tyroFormDivRef.current = null;

    requestAnimationFrame(() => {
      destroyTyroForm();
      setCancelling(false);
    });
  }

  if (showPaymentForm) {
    return (
      <main key="payment-form" className="checkout-page container">
        <h2 className="checkout-title">Payment</h2>

        <div className="checkout-summary card">
          <h3>Total: ${paymentTotal.toFixed(2)}</h3>
        </div>

        <form onSubmit={handleCardSubmit} className="tyro-card-form card">
          {tyroConfig.saveCardEnabled && user && (
            <div className="save-card-section">
              <label className="order-later-toggle">
                <input
                  type="checkbox"
                  checked={saveCard}
                  disabled={paymentProcessing || cancelling || saveCardUpdating}
                  onChange={(e) => handleSaveCardChange(e.target.checked)}
                />
                <span>Save this card for next time</span>
              </label>
              <p className="save-card-hint">
                Card details stay with Tyro. We only keep a token and the last four digits.
                Apple Pay and Google Pay cannot be saved. Changing this after you type reloads the card fields.
              </p>
            </div>
          )}

          <div id="tyro-pay-form" ref={tyroFormRefCallback} className="tyro-pay-form-container" />

          {tyroConfig.saveCardEnabled && !user && (
            <p className="save-card-signin">
              <Link to="/auth?return=/checkout">Sign in</Link> to save a card for faster checkout next time.
            </p>
          )}

          {paymentProcessing && <p className="wallet-processing">Processing payment...</p>}
          {error && <p className="error-text">{error}</p>}
          {show3dsBrowserFallback && (
            <button
              type="button"
              className="btn btn-primary btn-block checkout-submit"
              style={{ marginTop: '0.5rem' }}
              onClick={async () => {
                const baseUrl = isNativeApp()
                  ? (process.env.REACT_APP_API_URL || 'https://stbarbkitchen-fuekbybxgte3enen.australiaeast-01.azurewebsites.net')
                  : window.location.origin;
                const params = new URLSearchParams({
                  s: paySecret,
                  r: payRequestId || '',
                  o: (orderIds || []).join(','),
                  t: paymentTotal.toFixed(2),
                  n: orderResult?.order_number || '',
                  g: orderResult?.order_group || '',
                });
                const payUrl = `${baseUrl}/pay?${params.toString()}`;

                if (isNativeApp()) {
                  try {
                    const { Browser } = await import('@capacitor/browser');
                    const checkPaymentOnReturn = async () => {
                      try {
                        const payRequest = await tyroRef.current?.fetchPayRequest();
                        if (payRequest?.status === 'SUCCESS') {
                          await api.confirmPayment(orderIds, paySecret, payRequestId);
                          clearCart();
                          navigateToConfirmation(orderResult);
                        }
                      } catch (_) { /* user will retry if needed */ }
                    };
                    await Browser.addListener('browserFinished', checkPaymentOnReturn);
                    await Browser.open({ url: payUrl, presentationStyle: 'fullscreen' });
                  } catch (e) {
                    window.open(payUrl, '_blank');
                  }
                } else {
                  window.open(payUrl, '_blank');
                }
              }}
            >
              {isNativeApp() ? 'Complete Payment Securely' : 'Complete Payment in Safari'}
            </button>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-block checkout-submit"
            disabled={paymentProcessing || cancelling || saveCardUpdating}
          >
            {paymentProcessing ? 'Processing...' : saveCardUpdating ? 'Updating…' : `Pay $${paymentTotal.toFixed(2)}`}
          </button>

          <button
            type="button"
            className="btn btn-outline btn-block tyro-cancel-btn"
            onClick={handleCancelPayment}
            disabled={paymentProcessing || cancelling || saveCardUpdating}
          >
            {cancelling ? 'Cancelling...' : 'Cancel Payment'}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main key="checkout-form" className="checkout-page container">
      <h2 className="checkout-title">Checkout</h2>

      <div className="checkout-summary card">
        <h3>Order Summary</h3>
        {items.map((item) => (
          <div key={item._key} className="checkout-item">
            <span>
              {item.quantity}x {item.name}
              {item.optionsLabel && (
                <span className="checkout-item-options"> — {item.optionsLabel}</span>
              )}
            </span>
            <span>${(item.price * item.quantity).toFixed(2)}</span>
          </div>
        ))}
        <div className="checkout-total">
          <span>Total</span>
          <span>${total.toFixed(2)}</span>
        </div>
        <button
          type="button"
          className="checkout-add-more"
          onClick={() => navigate('/')}
        >
          &larr; Add more items
        </button>
      </div>

      <form onSubmit={handleSubmit} className="checkout-form">
        <div className="form-group">
          <label htmlFor="name">
            Your Name {nameRequired ? '(required for cafe / Turkish coffee)' : '(optional)'}
          </label>
          <input
            id="name"
            type="text"
            placeholder="e.g. John"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required={nameRequired}
          />
        </div>


        {hasCafeItems && scheduleForLaterEnabled && (
          <div className="order-later-section card">
            <label className="order-later-toggle">
              <input
                type="checkbox"
                checked={orderForLater}
                onChange={(e) => {
                  setOrderForLater(e.target.checked);
                  if (!e.target.checked) setPickupTime('');
                }}
              />
              <span>Order for Later (schedule pickup time)</span>
            </label>
            {orderForLater && (
              <div className="order-later-picker">
                <label htmlFor="pickup-time">Pickup Time</label>
                <select
                  id="pickup-time"
                  value={pickupTime}
                  onChange={(e) => setPickupTime(e.target.value)}
                  required={orderForLater}
                >
                  <option value="">Select a time...</option>
                  {timeSlots.map((slot) => (
                    <option key={slot.value} value={slot.value}>{slot.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {tyroConfig.saveCardEnabled && user && !(payWithSaved && selectedPayMethodId && savedPayMethods.length > 0) && (
          <div className="save-card-section card">
            <label className="order-later-toggle">
              <input
                type="checkbox"
                checked={saveCard}
                onChange={(e) => setSaveCard(e.target.checked)}
              />
              <span>Save this card for next time</span>
            </label>
            <p className="save-card-hint">
              Card details stay with Tyro. We only keep a token and the last four digits.
              Apple Pay and Google Pay cannot be saved.
            </p>
          </div>
        )}

        {tyroConfig.saveCardEnabled && user && savedPayMethods.length > 0 && (
          <div className="saved-card-section card">
            <label className="order-later-toggle">
              <input
                type="radio"
                name="card-choice"
                checked={payWithSaved}
                onChange={() => setPayWithSaved(true)}
              />
              <span>Pay with {savedPayMethods.find((m) => m.pay_method_id === selectedPayMethodId)?.label || savedPayMethods[0].label}</span>
            </label>
            {savedPayMethods.length > 1 && payWithSaved && (
              <select
                className="saved-card-select"
                value={selectedPayMethodId}
                onChange={(e) => setSelectedPayMethodId(e.target.value)}
              >
                {savedPayMethods.map((m) => (
                  <option key={m.pay_method_id} value={m.pay_method_id}>{m.label}</option>
                ))}
              </select>
            )}
            <label className="order-later-toggle">
              <input
                type="radio"
                name="card-choice"
                checked={!payWithSaved}
                onChange={() => setPayWithSaved(false)}
              />
              <span>Use a different card</span>
            </label>
          </div>
        )}

        <div className="payment-checkout-section">
          <button
            type="submit"
            className="btn btn-primary btn-block checkout-submit"
            disabled={submitting || cancelling}
          >
            {submitting ? 'Placing Order...' : `Pay $${total.toFixed(2)}`}
          </button>

          {error && <p className="error-text">{error}</p>}
        </div>
      </form>
    </main>
  );
}
